// v1.27.25 — Payment Modes admin page. Full CRUD: add, rename (cascades to
// every transaction), delete. Reachable from Administration menu. Renaming
// here updates the master mode row AND every historical transaction that used
// it, so the ledger, Payment Modes Report and exports all stay consistent.

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Plus, PencilSimple, Check, X, Trash, ArrowsMerge, SortAscending, TrendUp } from "@phosphor-icons/react";
import EmptyState from "@/components/EmptyState";

export default function PaymentModesAdmin() {
    const [modes, setModes] = useState([]);
    const [portals, setPortals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newName, setNewName] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [mergeSrc, setMergeSrc] = useState(null);
    const [mergeTargetId, setMergeTargetId] = useState("");
    // v1.27.28 — Sort toggle. Default is "usage" (max used first) to match
    // the order used everywhere else in the app. Admins can flip to A → Z
    // when they need to eyeball the list alphabetically (spot typos, dupes).
    const [sortMode, setSortMode] = useState("usage"); // "usage" | "az"
    const [selected, setSelected] = useState(() => new Set());
    const [bulkPortalId, setBulkPortalId] = useState("");
    const [onlyUnlinked, setOnlyUnlinked] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [md, pt] = await Promise.all([api.get("/payment-modes"), api.get("/portals")]);
            setModes(md.data || []);
            setPortals(pt.data || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const linkedPortal = (m) => {
        const key = (m.name || "").toLowerCase();
        return portals.find((p) => (p.mode_names || []).some((x) => (x || "").toLowerCase() === key));
    };

    const assignPortal = async (m, portalId) => {
        const current = linkedPortal(m);
        if ((current?.id || "") === (portalId || "")) return;
        const target = portals.find((p) => p.id === portalId);
        if (portalId && current && current.id !== portalId) {
            if (!window.confirm(`This mode is already linked to "${current.name}". Reassign to "${target?.name}"?`)) return;
        }
        try {
            await api.post("/payment-modes/assign-portal", { mode_name: m.name, portal_id: portalId || null, reassign: true });
            toast.success(portalId ? `Linked "${m.name}" → ${target?.name}` : `Unlinked "${m.name}"`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const toggleSelect = (name) => setSelected((prev) => {
        const n = new Set(prev); const k = (name || "").toLowerCase();
        n.has(k) ? n.delete(k) : n.add(k);
        return n;
    });
    const bulkAssign = async (portalId) => {
        const names = modes.filter((m) => selected.has((m.name || "").toLowerCase())).map((m) => m.name);
        if (!names.length) return;
        const target = portals.find((p) => p.id === portalId);
        if (portalId) {
            const conflicts = names.filter((n) => { const lp = linkedPortal({ name: n }); return lp && lp.id !== portalId; });
            if (conflicts.length && !window.confirm(`${conflicts.length} selected mode(s) are already linked to another portal. Reassign all to "${target?.name}"?`)) return;
        }
        try {
            await api.post("/payment-modes/assign-portal-bulk", { mode_names: names, portal_id: portalId || null });
            toast.success(portalId ? `Linked ${names.length} mode(s) → ${target?.name}` : `Unlinked ${names.length} mode(s)`);
            setSelected(new Set()); setBulkPortalId("");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const add = async () => {
        const name = newName.trim();
        if (!name) return;
        try {
            await api.post("/payment-modes", { name });
            toast.success(`Added "${name}"`);
            setNewName("");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // v1.28.81 — Formalise an auto-detected mode (one-click "Add to master").
    const addFromGhost = async (name) => {
        try {
            await api.post("/payment-modes", { name });
            toast.success(`Added "${name}" to master`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const beginEdit = (m) => { setEditingId(m.id); setEditValue(m.name); };
    const cancelEdit = () => { setEditingId(null); setEditValue(""); };
    const saveEdit = async (m) => {
        const name = editValue.trim();
        if (!name || name === m.name) { cancelEdit(); return; }
        try {
            const { data } = await api.patch(`/payment-modes/${m.id}`, { name });
            const n = data?.txns_updated || 0;
            toast.success(`Renamed → "${name}"${n ? ` · ${n} transaction${n === 1 ? "" : "s"} updated` : ""}`);
            cancelEdit();
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async (m) => {
        if (!window.confirm(`Delete "${m.name}"?\n\nExisting transactions using this mode will keep the label — this only removes it from the dropdown.`)) return;
        try {
            await api.delete(`/payment-modes/${m.id}`);
            toast.success(`Deleted "${m.name}"`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const doMerge = async () => {
        if (!mergeSrc || !mergeTargetId) return;
        const tgt = modes.find((m) => m.id === mergeTargetId);
        if (!tgt) return;
        // v1.27.41 — Fetch the actual ledger-row count first so the confirm
        // dialog shows the admin exactly what's about to change.
        let preview;
        try {
            const { data } = await api.get(`/payment-modes/${mergeSrc.id}/merge-preview/${mergeTargetId}`);
            preview = data;
        } catch (e) { toast.error(formatApiError(e)); return; }
        const n = preview.txns_to_update || 0;
        const msg = [
            `Merge Payment Mode Confirmation`,
            ``,
            `Old mode to remove   : "${preview.source_name}"`,
            `Merge into new mode  : "${preview.target_name}"`,
            `Ledger rows to update: ${n.toLocaleString("en-IN")}`,
            ``,
            `Every transaction currently labelled "${preview.source_name}" (case-insensitive) will be re-tagged to "${preview.target_name}". "${preview.source_name}" will then be deleted from the modes list.`,
            ``,
            `This action is irreversible. Continue?`,
        ].join("\n");
        if (!window.confirm(msg)) return;
        try {
            const { data } = await api.post(`/payment-modes/${mergeSrc.id}/merge-into/${mergeTargetId}`);
            const parts = [`Merged "${mergeSrc.name}" → "${tgt.name}"`];
            if (data?.txns_updated) parts.push(`${data.txns_updated} transaction${data.txns_updated === 1 ? "" : "s"} updated`);
            if (data?.remaining_with_old_name > 0) {
                toast.warning(`${parts.join(" · ")} · ⚠ ${data.remaining_with_old_name} still show the old name — please contact support`);
            } else {
                toast.success(`${parts.join(" · ")} · verified 0 stragglers`);
            }
            setMergeSrc(null); setMergeTargetId("");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const visibleModes = modes.filter((m) => !onlyUnlinked || !linkedPortal(m));
    const allVisibleSelected = visibleModes.length > 0 && visibleModes.every((m) => selected.has((m.name || "").toLowerCase()));
    const toggleSelectAll = () => setSelected(() => allVisibleSelected ? new Set() : new Set(visibleModes.map((m) => (m.name || "").toLowerCase())));

    return (
        <div className="space-y-6" data-testid="payment-modes-admin-page">
            <div>
                <Link to="/admin" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-800">
                    <ArrowLeft size={12} /> Back to Dashboard
                </Link>
                <h1 className="mt-1 font-display text-4xl font-bold tracking-tight text-slate-900">Manage Payment Modes</h1>
                <p className="mt-1 text-sm text-slate-600">Add, rename or delete the modes that appear in every payment dialog. Renaming here cascades to every historical transaction that used the old name.</p>
            </div>

            <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Add a new mode</Label>
                <div className="mt-2 flex items-center gap-2">
                    <Input
                        data-testid="pm-admin-add-input"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                        placeholder="e.g. PhonePe, Amazon Pay, Distributor A"
                        className="h-9 bg-white text-sm"
                    />
                    <Button type="button" data-testid="pm-admin-add-btn" onClick={add} disabled={!newName.trim()} className="h-9 bg-emerald-600 text-white hover:bg-emerald-700">
                        <Plus size={14} className="mr-1" /> Add
                    </Button>
                </div>
            </section>

            <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-100 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="font-display text-sm font-semibold text-slate-900">All Payment Modes</div>
                    <div className="flex items-center gap-3">
                        <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5" data-testid="pm-sort-toggle">
                            <button
                                type="button"
                                data-testid="pm-sort-usage-btn"
                                onClick={() => setSortMode("usage")}
                                className={`inline-flex items-center gap-1 rounded px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider transition ${sortMode === "usage" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                            >
                                <TrendUp size={12} weight="bold" /> Max Used
                            </button>
                            <button
                                type="button"
                                data-testid="pm-sort-az-btn"
                                onClick={() => setSortMode("az")}
                                className={`inline-flex items-center gap-1 rounded px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider transition ${sortMode === "az" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                            >
                                <SortAscending size={12} weight="bold" /> A → Z
                            </button>
                        </div>
                        <button
                            type="button"
                            data-testid="pm-filter-unlinked"
                            onClick={() => setOnlyUnlinked((v) => !v)}
                            className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider transition ${onlyUnlinked ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-200 bg-white text-slate-500 hover:text-slate-700"}`}
                        >
                            Only Unlinked
                        </button>
                        <div className="text-[11px] text-slate-500">{modes.length} mode{modes.length === 1 ? "" : "s"}</div>
                    </div>
                </div>
                {selected.size > 0 && (
                    <div className="sticky bottom-20 z-20 flex flex-wrap items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2.5 shadow-lg md:static md:bottom-auto md:rounded-none md:border-0 md:border-b md:border-indigo-100 md:bg-indigo-50/60 md:shadow-none" data-testid="pm-bulk-bar">
                        <span className="text-xs font-semibold text-indigo-900">{selected.size} selected</span>
                        <select
                            data-testid="pm-bulk-portal-select"
                            value={bulkPortalId}
                            onChange={(e) => setBulkPortalId(e.target.value)}
                            className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs"
                        >
                            <option value="">Pick a portal…</option>
                            {portals.filter((p) => p.active !== false).map((p) => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </select>
                        <Button type="button" size="sm" data-testid="pm-bulk-link-btn" disabled={!bulkPortalId} onClick={() => bulkAssign(bulkPortalId)} className="h-8 bg-indigo-600 text-white hover:bg-indigo-700">Link selected</Button>
                        <Button type="button" size="sm" variant="outline" data-testid="pm-bulk-unlink-btn" onClick={() => bulkAssign("")} className="h-8">Unlink selected</Button>
                        <button type="button" data-testid="pm-bulk-clear" onClick={() => setSelected(new Set())} className="text-xs text-slate-500 underline hover:text-slate-800">Clear</button>
                    </div>
                )}
                {loading && <div className="p-6 text-sm text-slate-500">Loading…</div>}
                {!loading && modes.length === 0 && (
                    <div className="p-6"><EmptyState icon={Plus} title="No payment modes yet" description="Add your first mode above." /></div>
                )}
                {modes.length > 0 && (
                    <table className="min-w-full text-sm" data-testid="pm-admin-table">
                        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="w-8 px-3 py-2 text-left"><input type="checkbox" data-testid="pm-select-all" checked={allVisibleSelected} onChange={toggleSelectAll} /></th>
                                <th className="px-4 py-2 text-left">Name</th>
                                <th className="px-3 py-2 text-right">Usage</th>
                                <th className="px-3 py-2 text-left">Linked Portal</th>
                                <th className="px-3 py-2 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {[...visibleModes].sort((a, b) => {
                                if (sortMode === "az") {
                                    return (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
                                }
                                // Default: master rows before ghosts, max used first, then most-recent, then name.
                                const ga = a.auto_detected ? 1 : 0;
                                const gb = b.auto_detected ? 1 : 0;
                                if (ga !== gb) return ga - gb;
                                const uc = (b.usage_count || 0) - (a.usage_count || 0);
                                if (uc !== 0) return uc;
                                const la = a.last_used || "";
                                const lb = b.last_used || "";
                                if (la !== lb) return lb.localeCompare(la);
                                return (a.name || "").localeCompare(b.name || "");
                            }).map((m) => (
                                <tr key={m.id || `ghost-${m.name_lower}`} className="hover:bg-slate-50" data-testid={m.id ? `pm-row-${m.id}` : `pm-row-ghost-${m.name_lower}`}>
                                    <td className="px-3 py-2"><input type="checkbox" data-testid={`pm-select-${m.id || m.name_lower}`} checked={selected.has((m.name || "").toLowerCase())} onChange={() => toggleSelect(m.name)} /></td>
                                    <td className="px-4 py-2">
                                        {editingId === m.id ? (
                                            <Input
                                                data-testid={`pm-edit-input-${m.id}`}
                                                value={editValue}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === "Enter") saveEdit(m); if (e.key === "Escape") cancelEdit(); }}
                                                autoFocus
                                                className="h-8 max-w-xs bg-white text-sm"
                                            />
                                        ) : (
                                            <span className="inline-flex items-center gap-2">
                                                <span className="font-medium text-slate-900">{m.name}</span>
                                                {m.auto_detected && (
                                                    <span
                                                        data-testid={`pm-auto-detected-${m.name_lower}`}
                                                        title="This mode exists on real transactions but hasn't been added to the master list. Click 'Add to master' to formalise it."
                                                        className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-800"
                                                    >
                                                        Auto-detected
                                                    </span>
                                                )}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono text-xs text-slate-600">{m.usage_count || 0} uses</td>
                                    <td className="px-3 py-2">
                                        <div className="flex items-center gap-2">
                                            <select
                                                data-testid={`pm-portal-select-${m.id || m.name_lower}`}
                                                value={linkedPortal(m)?.id || ""}
                                                onChange={(e) => assignPortal(m, e.target.value)}
                                                className="h-8 max-w-[160px] rounded-md border border-slate-300 bg-white px-2 text-xs"
                                            >
                                                <option value="">— Unlinked —</option>
                                                {portals.filter((p) => p.active !== false).map((p) => (
                                                    <option key={p.id} value={p.id}>{p.name}</option>
                                                ))}
                                            </select>
                                            {!linkedPortal(m) && (
                                                <span
                                                    data-testid={`pm-unlinked-${m.id || m.name_lower}`}
                                                    title="Not linked to any portal — assign one if this mode is a portal withdrawal."
                                                    className="rounded-full border border-slate-300 bg-slate-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-500"
                                                >
                                                    Unlinked
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        {m.auto_detected ? (
                                            <Button
                                                type="button" size="sm"
                                                onClick={() => addFromGhost(m.name)}
                                                data-testid={`pm-add-ghost-${m.name_lower}`}
                                                className="h-7 bg-emerald-600 text-white hover:bg-emerald-700"
                                            >
                                                <Plus size={13} className="mr-1" weight="bold" /> Add to master
                                            </Button>
                                        ) : editingId === m.id ? (
                                            <div className="inline-flex items-center gap-1">
                                                <Button type="button" size="sm" onClick={() => saveEdit(m)} className="h-7 bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`pm-save-${m.id}`}>
                                                    <Check size={13} className="mr-0.5" /> Save
                                                </Button>
                                                <Button type="button" size="sm" variant="outline" onClick={cancelEdit} className="h-7" data-testid={`pm-cancel-${m.id}`}>
                                                    <X size={13} />
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="inline-flex items-center gap-1">
                                                <Button type="button" size="sm" variant="outline" onClick={() => beginEdit(m)} className="h-7 border-slate-300" data-testid={`pm-edit-${m.id}`}>
                                                    <PencilSimple size={13} className="mr-1" /> Rename
                                                </Button>
                                                <Button type="button" size="sm" variant="outline" onClick={() => { setMergeSrc(m); setMergeTargetId(""); }} className="h-7 border-violet-300 text-violet-700 hover:bg-violet-50" data-testid={`pm-merge-${m.id}`}>
                                                    <ArrowsMerge size={13} className="mr-1" /> Merge
                                                </Button>
                                                <Button type="button" size="sm" variant="outline" onClick={() => remove(m)} className="h-7 border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`pm-delete-${m.id}`}>
                                                    <Trash size={13} />
                                                </Button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 text-xs text-amber-900">
                <b>Rename cascades:</b> When you rename a mode, every historical transaction that used the old name is updated to the new name. Reports, ledger and exports all reflect the correction immediately.
            </div>

            {/* v1.27.27 — Merge dialog. Source mode is selected via row action;
                target is picked here. Every txn labelled with source gets
                re-tagged to target and source row is deleted. */}
            {mergeSrc && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="pm-merge-dialog">
                    <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
                        <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.24em] text-violet-700">Merge Payment Mode</div>
                        <h2 className="font-display text-xl font-bold text-slate-900">Merge "{mergeSrc.name}" into…</h2>
                        <p className="mt-1 text-xs text-slate-600">Every transaction currently labelled <b>"{mergeSrc.name}"</b> will be re-tagged to the target you pick. "{mergeSrc.name}" will be deleted from the modes list.</p>
                        <div className="mt-4">
                            <Label className="text-xs uppercase tracking-wider text-slate-500">Target mode</Label>
                            <select
                                data-testid="pm-merge-target-select"
                                value={mergeTargetId}
                                onChange={(e) => setMergeTargetId(e.target.value)}
                                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                            >
                                <option value="">Pick target…</option>
                                {modes.filter((m) => m.id !== mergeSrc.id).map((m) => (
                                    <option key={m.id} value={m.id}>{m.name} ({m.usage_count || 0} uses)</option>
                                ))}
                            </select>
                        </div>
                        <div className="mt-5 flex justify-end gap-2">
                            <Button type="button" variant="outline" onClick={() => { setMergeSrc(null); setMergeTargetId(""); }} className="h-9">Cancel</Button>
                            <Button type="button" onClick={doMerge} disabled={!mergeTargetId} data-testid="pm-merge-confirm-btn" className="h-9 bg-violet-600 text-white hover:bg-violet-700">
                                <ArrowsMerge size={14} className="mr-1" /> Merge
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
